import React, { useEffect } from 'react';
import { Col, DatePicker, Form, FormInstance, Input, Modal, Row, Select } from 'antd';
import { CarOutlined, DashboardOutlined, ToolOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  getShowAddNewServiceModal,
  getShowEditServiceModal,
  showAddNewServiceModal,
  showEditServiceModal,
} from '../../store/app-ui.store';
import { getVehicles } from '../../store/vehicle.store';
import { addNewServiceRecord, editServiceRecord } from '../../store/service.store';
import ServiceRecordFormValues, {
  formValuesToPayload,
  formValuesToUpdatePayload,
  recordToFormValues,
} from '../../Types/ServiceRecord';

const useVehicleList = () => {
  const raw: any = useAppSelector(getVehicles);
  return Array.isArray(raw) ? raw : raw?.data ?? [];
};
const useVehicleOptions = () => useVehicleList().map((v: any) => ({ label: v.plateNumber, value: v.plateNumber }));
const ServiceRecordForm = ({
  formRef,
  handleSubmit,
  isEditing,
  vehicleOptions,
}: {
  formRef: FormInstance;
  handleSubmit: (values: ServiceRecordFormValues) => void;
  isEditing: boolean;
  vehicleOptions: { label: string; value: string }[];
}) => (
  <Form layout="vertical" form={formRef} name="service-record-form" onFinish={handleSubmit} initialValues={initValue}>
    <Form.Item label="Vehicle Number" name="vehicle_no" rules={validations.vehicle_no}>
      <Select placeholder="Select Vehicle" options={vehicleOptions} disabled={isEditing} suffixIcon={<CarOutlined />} />
    </Form.Item>

    <Row justify="space-between">
      <Col span={11}>
        <Form.Item label="Current Meter Reading (ODO)" name="meterReading" rules={validations.meterReading}>
          <Input placeholder="e.g. 750" prefix={<DashboardOutlined />} suffix="km" />
        </Form.Item>
      </Col>
      <Col span={11}>
        <Form.Item label="Service Date" name="date" rules={validations.date}>
          <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
        </Form.Item>
      </Col>
    </Row>

    <Row justify="space-between">
      <Col span={11}>
        <Form.Item label="Next Service Date" name="nextServiceDate" rules={validations.nextServiceDate}>
          <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
        </Form.Item>
      </Col>
      <Col span={11}>
        <Form.Item
          label="Next Service Meter Reading"
          name="nextServiceMeterReading"
          rules={validations.nextServiceMeterReading}
        >
          <Input placeholder="e.g. 1000" prefix={<ToolOutlined />} suffix="km" />
        </Form.Item>
      </Col>
    </Row>
  </Form>
);

type AddNewServiceRecordProps = {
  onSuccess?: (vehicleId: number) => void;
};

const AddNewServiceRecord: React.FC<AddNewServiceRecordProps> = ({ onSuccess }) => {
  const visible = useAppSelector(getShowAddNewServiceModal);
  const vehicles = useVehicleList();
  const vehicleOptions = useVehicleOptions();
  const dispatch = useAppDispatch();
  const [formRef] = Form.useForm();

  const handleOk = () => formRef.submit();

  const handleCancel = () => {
    formRef.resetFields();
    dispatch(showAddNewServiceModal(false));
  };

  const handleSubmit = async (values: ServiceRecordFormValues) => {
    const vehicle = vehicles.find((v: any) => v.plateNumber === values.vehicle_no);
    const vehicleId = vehicle?.vehicle_id;
    if (!vehicleId) return;
    await dispatch(addNewServiceRecord(formValuesToPayload(values, vehicleId)));
    formRef.resetFields();
    onSuccess?.(vehicleId);
  };

  return (
    <Modal
      visible={visible}
      onOk={handleOk}
      title="Add New Service Record"
      destroyOnClose
      onCancel={handleCancel}
      okText="Save Record"
    >
      <ServiceRecordForm
        isEditing={false}
        formRef={formRef}
        handleSubmit={handleSubmit}
        vehicleOptions={vehicleOptions}
      />
    </Modal>
  );
};

AddNewServiceRecord.defaultProps = {
  onSuccess: undefined,
};

export const EditServiceModal = (): JSX.Element => {
  const record = useAppSelector(getShowEditServiceModal);
  const vehicles = useVehicleList();
  const vehicleOptions = useVehicleOptions();
  const dispatch = useAppDispatch();
  const [formRef] = Form.useForm();

  useEffect(() => {
    if (record) {
      const plate =
        vehicles.find((v: any) => v.vehicle_id === record.vehicle_id)?.plateNumber ?? String(record.vehicle_id);
      formRef.setFieldsValue(recordToFormValues(record, plate));
    }
  }, [record]);

  const handleOk = () => formRef.submit();

  const handleCancel = () => {
    formRef.resetFields();
    dispatch(showEditServiceModal(null));
  };

  const handleSubmit = (values: ServiceRecordFormValues) => {
    if (!record) return;
    dispatch(
      editServiceRecord({
        id: record.id,
        payload: formValuesToUpdatePayload(values, record.vehicle_id),
        vehicleId: record.vehicle_id,
      }),
    );
  };

  return (
    <Modal
      visible={!!record}
      onOk={handleOk}
      title="Edit Service Record"
      destroyOnClose
      onCancel={handleCancel}
      okText="Save Record"
    >
      {record && (
        <ServiceRecordForm isEditing formRef={formRef} handleSubmit={handleSubmit} vehicleOptions={vehicleOptions} />
      )}
    </Modal>
  );
};

const initValue: ServiceRecordFormValues = {
  vehicle_no: undefined as unknown as string,
  meterReading: '',
  date: null,
  nextServiceDate: null,
  nextServiceMeterReading: '',
};

const validations = {
  vehicle_no: [{ required: true, message: 'Please select a Vehicle' }],
  meterReading: [{ required: true, message: 'Please enter the Current Meter Reading' }],
  date: [{ required: true, message: 'Please select the Service Date' }],
  nextServiceDate: [{ required: true, message: 'Please select the Next Service Date' }],
  nextServiceMeterReading: [{ required: true, message: 'Please enter the Next Service Meter Reading' }],
};

export default AddNewServiceRecord;
